<script setup lang='ts'>
import { computed, inject, ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import FileReader from '@/views/partials/components/attachments/FIleReader.vue';
import { FilePreview, Resource  } from '@/modules/core/files/services/StorageService';
import { FormBuilderContract } from '@/components/builder/form';
import { $i18n, $t } from '@/plugins/localization';
import properties from '../../properties';
import { DocumentType } from '../logito-document';
import { FormEntry } from '../form';
import { useMixins } from '@/plugins/mixins';
import { DocumentPreviewHeight, LogitoDocumentPreviewType } from '.';

defineOptions({
    name: 'logito-document-preview-blueprint',
    components: {
        ...properties,
    },
});

const props = defineProps({
  "blueprint": null,
  "entry": null,
  "form": null,
  "parent": null,
  "index": null
});

const { $filters } = useMixins();

const wrapperRef = ref<HTMLElement | null>(null);
const documentPreviewContentRef = ref<HTMLElement | null>(null);
const offsetTop = ref('0');
const maxHeight = ref('100%');

const locale = $i18n.locale();

const filePreview = ref<Resource | null>(null);
const fileUrl = computed(() => filePreview.value?.publicId ? `api/documents/file/${filePreview.value.publicId}/${filePreview.value.name}` : filePreview.value?.url);
const filesPreview = inject<FilePreview[]>('filePreview');

const blueprint = computed(() => props.blueprint);
const selectedOption = computed(() => props.blueprint.fieldName);
const documentComponents = computed(() =>
    props.form.schema
        .components("document")
        .map((p) => ({
            value: p.name,
            text: props.form.localization.translate((p as DocumentType).label),
        }))
);

watch(
    filesPreview,
    () =>
    {
        if (!selectedOption.value)
        {
            filePreview.value = null;

            return;
        }

        setFilePreview();
    },
    { deep: true }
);

function setFilePreview()
{
    const foundFilePreview = filesPreview?.value.find(
        (p: FilePreview) => p.documentControlName === selectedOption.value
    );

    filePreview.value = foundFilePreview?.file || filesPreview?.value[0]?.file || null;

    nextTick(() =>
    {
        calculateMaxHeight();
    });
}

const init = () =>
{
    if (!selectedOption.value)
    {
        filePreview.value = null;

        return;
    }

    setFilePreview();
};

function calculateMaxHeight()
{
    if (documentPreviewContentRef.value && window.innerWidth >= 768)
    {
        const navbarHeight = document.querySelector('.navbar')?.clientHeight ?? 0;
        const breadcrumbsHeight = document.querySelector('#breadcrumbs')?.clientHeight ?? 0;
        const toolbarHeight = document.querySelector('.toolbar')?.clientHeight ?? 0;

        maxHeight.value = `calc(100vh - ${navbarHeight + breadcrumbsHeight + toolbarHeight + 20}px)`;

        offsetTop.value = `${toolbarHeight + 10}px`;
    }
    else
    {
        maxHeight.value = '';
        offsetTop.value = '';
    }
}

onMounted(() =>
{
    wrapperRef.value = document.querySelector('[data-name="wrapper"]');

    nextTick(() =>
    {
        calculateMaxHeight();

        setTimeout(calculateMaxHeight, 100);

        if (document.readyState === 'complete')
        {
            calculateMaxHeight();
        }
        else
        {
            window.addEventListener('load', calculateMaxHeight, { once: true });
        }
    });

    window.addEventListener('resize', calculateMaxHeight);
});

onBeforeUnmount(() =>
{
    window.removeEventListener('resize', calculateMaxHeight);
});

init();
</script>

<template>
    <form-component-wrapper :form="form" :parent="parent" :blueprint="blueprint" :table-row-index="props.index">
        <template #default>
            <div class="form-group">
                <div v-if="filePreview && filePreview.url" ref="documentPreviewContentRef" class="position-relative h-100">
                    <file-reader :src="fileUrl" class="w-100 h-100" :style="{ 'max-height': maxHeight, 'top': offsetTop }" />
                </div>
                <span v-else>{{ $t('[[[Wybierz dokument do podglądu]]]') }}</span>
            </div>
        </template>
        <template #properties>
            <field-name :form="form" :blueprint="blueprint" v-model="blueprint.label[locale]" />
            <field-select
                v-if="documentComponents"
                :options="documentComponents"
                v-model="blueprint.fieldName"
                :label="$t('[[[Powiązane dokumenty]]]')"
                required
            />
        </template>
    </form-component-wrapper>
</template>
